import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../api';
import DateRange from '../components/DateRange';

const COLORS = { openai_gpt: '#00d4ff', openai_tts: '#34d399', firebase_functions: '#fbbf24', firebase_firestore: '#fb923c', firebase_storage: '#a78bfa', places_api: '#f472b6', hetzner: '#f87171', ai_log_analyzer: '#c084fc', app_engine: '#fb923c', cloud_run_functions: '#fbbf24' };
// A GCP számláján a Firestore-olvasás "App Engine" néven jelenik meg (a Firestore
// a Datastore-ból nőtt ki) — ezért írjuk oda zárójelben, hogy ne kelljen keresni.
const LABELS = { openai_gpt: 'GPT-4o-mini', openai_tts: 'TTS-1', firebase_functions: 'Cloud Functions', firebase_firestore: 'Firestore', firebase_storage: 'Storage', places_api: 'Places API', hetzner: 'Hetzner', ai_log_analyzer: '🤖 AI log elemző', app_engine: 'App Engine (Firestore)', cloud_run_functions: 'Cloud Run functions' };

const STATUS_STYLE = {
  ok: { color: '#22c55e', icon: '✅', text: 'rendben' },
  not_configured: { color: '#fbbf24', icon: '⚠️', text: 'nincs beállítva' },
  error: { color: '#ef4444', icon: '❌', text: 'hiba' },
  never_ran: { color: '#8892a0', icon: '—', text: 'még sosem futott' },
};

function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; }
function today() { return new Date().toISOString().split('T')[0]; }

export default function Costs() {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [daily, setDaily] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    api.costs.daily(from, to).then(setDaily).catch(() => {});
    api.costs.monthly(new Date().getFullYear().toString()).then(setMonthly).catch(() => {});
  }, [from, to]);

  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState(null);

  const loadHealth = () => api.costs.health().then(setHealth).catch(() => {});
  useEffect(() => { loadHealth(); }, []);

  // 🆕 Kézi futtatás: a "még sosem futott" állapotból enélkül csak a következő
  //   06:00-s cron után derülne ki, mi a baj — ezzel azonnal megtudható.
  const runNow = async () => {
    setRunning(true); setRunMsg(null);
    try {
      const r = await api.manual.triggerFetch();
      const bad = (r.results || []).filter((x) => x.status !== 'ok');
      setRunMsg(bad.length
        ? `Lefutott mind a ${r.results.length} forrás (${r.date}) — ${bad.length} nem adott adatot, a részletek lent.`
        : `✅ Mind a ${r.results.length} forrás sikeresen lefutott (${r.date}).`);
      await loadHealth();
      api.costs.daily(from, to).then(setDaily).catch(() => {});
    } catch (e) {
      setRunMsg('❌ A futtatás nem sikerült: ' + e.message);
    } finally { setRunning(false); }
  };

  // Aggregate daily by date for chart
  const byDate = {};
  daily.forEach(r => {
    if (!byDate[r.date]) byDate[r.date] = { date: r.date };
    byDate[r.date][r.service] = (byDate[r.date][r.service] || 0) + r.amount_usd;
  });
  const chartData = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));

  // Pie data
  const byService = {};
  daily.forEach(r => { byService[r.service] = (byService[r.service] || 0) + r.amount_usd; });
  const pieData = Object.entries(byService).map(([k, v]) => ({ name: LABELS[k] || k, value: Math.round(v * 100) / 100 }));

  const services = [...new Set(daily.map(r => r.service))];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Költségek</h1>
        <DateRange from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
      </div>

      {/* 🆕 Adatforrás-állapot. Enélkül a hiányzó adat és a nulla költség
          pontosan egyformán néz ki — és pont ez rejtette el hónapokig, hogy a
          napi lekérés fele el sem indult. */}
      {health && health.problems > 0 && (
        <div style={{
          background: '#22252f', border: '1px solid #fbbf24', borderRadius: 12,
          padding: 16, marginBottom: 20,
        }}>
          <div style={{ fontWeight: 700, color: '#fbbf24', marginBottom: 4 }}>
            ⚠️ {health.problems} adatforrás nem szolgáltat adatot
          </div>
          <div style={{ fontSize: 12, color: '#8892a0', marginBottom: 12, lineHeight: 1.5 }}>
            Az alábbi költségek <strong>hiányoznak</strong> a grafikonokról — ez nem azt jelenti,
            hogy nulla, hanem azt, hogy nem sikerült lekérdezni.
            {health.sources.every((s) => s.status === 'never_ran') && (
              <> A napi gyűjtés <strong>06:00-kor</strong> fut; a „Futtatás most" gombbal
              nem kell megvárnod, hogy kiderüljön, melyik forrással mi a baj.</>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <button
              onClick={runNow}
              disabled={running}
              style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid #fbbf24',
                background: running ? '#2a2e3a' : '#fbbf24', color: running ? '#8892a0' : '#1a1d27',
                fontWeight: 700, fontSize: 13, cursor: running ? 'default' : 'pointer',
              }}
            >
              {running ? '⏳ Fut… (akár 1-2 perc)' : '▶︎ Futtatás most'}
            </button>
            {runMsg && <span style={{ fontSize: 12, color: '#8892a0' }}>{runMsg}</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {health.sources.filter((s) => s.status !== 'ok').map((s) => {
              const st = STATUS_STYLE[s.status] || STATUS_STYLE.error;
              return (
                <div key={s.source} style={{ fontSize: 13, lineHeight: 1.5 }}>
                  <span style={{ color: st.color, fontWeight: 700 }}>{st.icon} {s.label}</span>
                  <span style={{ color: '#8892a0' }}> — {st.text}</span>
                  {s.message && <div style={{ color: '#8892a0', fontSize: 12, marginLeft: 22 }}>{s.message}</div>}
                  {s.last_success_at && (
                    <div style={{ color: '#8892a0', fontSize: 11, marginLeft: 22 }}>
                      Utoljára sikeres: {new Date(s.last_success_at).toLocaleString('hu-HU')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>Napi költségek szolgáltatásonként</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2e3a" />
              <XAxis dataKey="date" tick={{ fill: '#8b8fa3', fontSize: 11 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fill: '#8b8fa3', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb' }} />
              <Legend />
              {services.map(s => (
                <Line key={s} type="monotone" dataKey={s} stroke={COLORS[s] || '#888'} name={LABELS[s] || s} dot={false} strokeWidth={2} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>Megoszlás</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {pieData.map((_, i) => <Cell key={i} fill={Object.values(COLORS)[i % Object.values(COLORS).length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, color: '#e4e6eb' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <h3 style={{ fontSize: 15, marginBottom: 16, color: '#8b8fa3' }}>Havi összesítés ({new Date().getFullYear()})</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2e3a' }}>
              <th style={th}>Hónap</th><th style={th}>Szolgáltatás</th><th style={th}>USD</th><th style={th}>HUF</th>
            </tr>
          </thead>
          <tbody>
            {monthly.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #1a1d27' }}>
                <td style={td}>{r.month}</td>
                <td style={td}>{LABELS[r.service] || r.service}</td>
                <td style={{ ...td, color: '#f87171' }}>${r.total_usd?.toFixed(2)}</td>
                <td style={td}>{r.total_huf?.toLocaleString('hu-HU')} Ft</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 12px', color: '#8b8fa3', fontWeight: 600 };
const td = { padding: '8px 12px', color: '#e4e6eb' };
